import { Button } from "@aurumiq/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@aurumiq/ui/components/dropdown-menu";
import { ReasonDialog } from "@aurumiq/ui/composite/reason-dialog";
import { ArrowUpRight, Ban, MoreHorizontal, Pencil, Rocket } from "lucide-react";
import { useState } from "react";
import { usePermission } from "@/auth/use-permission";
import type { Release } from "@/lib/types";
import { usePromoteRelease, usePublishRelease, useYankRelease } from "./queries";

type Pending = { kind: "publish" | "yank" | "promote"; release: Release } | null;

/**
 * Row / detail actions for a release. Confirmations run through ReasonDialog so every
 * state change lands in the audit log; `publish` and `promote` accept an empty reason.
 */
export function ReleaseActions({
  release,
  onEdit,
  variant = "menu",
}: {
  release: Release;
  onEdit: (release: Release) => void;
  variant?: "menu" | "buttons";
}) {
  const canWrite = usePermission("releases:write");
  const publish = usePublishRelease();
  const yank = useYankRelease();
  const promote = usePromoteRelease();
  const [pending, setPending] = useState<Pending>(null);
  if (!canWrite) return null;

  const canPublish = release.status === "draft";
  const canYank = release.status === "published";
  const canPromote = release.channel === "beta" && release.status === "published";

  const items = (
    <>
      <DropdownMenuItem onSelect={() => onEdit(release)}>
        <Pencil /> Edit notes / staging
      </DropdownMenuItem>
      {canPublish ? (
        <DropdownMenuItem onSelect={() => setPending({ kind: "publish", release })}>
          <Rocket /> Publish
        </DropdownMenuItem>
      ) : null}
      {canPromote ? (
        <DropdownMenuItem onSelect={() => setPending({ kind: "promote", release })}>
          <ArrowUpRight /> Promote to stable
        </DropdownMenuItem>
      ) : null}
      {canYank ? (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive"
            onSelect={() => setPending({ kind: "yank", release })}
          >
            <Ban /> Yank
          </DropdownMenuItem>
        </>
      ) : null}
    </>
  );

  return (
    <>
      {variant === "menu" ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={`Actions for ${release.version}`}>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">{items}</DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => onEdit(release)}>
            <Pencil /> Edit
          </Button>
          {canPublish ? (
            <Button variant="gold" onClick={() => setPending({ kind: "publish", release })}>
              <Rocket /> Publish
            </Button>
          ) : null}
          {canPromote ? (
            <Button onClick={() => setPending({ kind: "promote", release })}>
              <ArrowUpRight /> Promote to stable
            </Button>
          ) : null}
          {canYank ? (
            <Button variant="destructive" onClick={() => setPending({ kind: "yank", release })}>
              <Ban /> Yank
            </Button>
          ) : null}
        </div>
      )}

      <ReasonDialog
        open={pending?.kind === "publish"}
        onOpenChange={(o) => !o && setPending(null)}
        title={`Publish ${release.version} to ${release.channel}?`}
        description={
          release.artifacts.some((a) => !a.uploaded)
            ? "Some artifacts have not finished uploading. Devices will only see the release once every file is present."
            : "Devices on this channel will be offered the update at the configured staging percentage."
        }
        confirmLabel="Publish"
        confirmVariant="default"
        minLength={0}
        reasonLabel="Note"
        onConfirm={(reason) => publish.mutateAsync({ id: release.id, reason: reason || undefined })}
      />
      <ReasonDialog
        open={pending?.kind === "yank"}
        onOpenChange={(o) => !o && setPending(null)}
        title={`Yank ${release.version}?`}
        description="The release is withdrawn from the update feed. Devices already updated keep it; new checks fall back to the previous version."
        confirmLabel="Yank release"
        onConfirm={(reason) => yank.mutateAsync({ id: release.id, reason })}
      />
      <ReasonDialog
        open={pending?.kind === "promote"}
        onOpenChange={(o) => !o && setPending(null)}
        title={`Promote ${release.version} to stable?`}
        description="Copies this beta release (notes and artifacts) into a stable draft that you can then publish."
        confirmLabel="Promote"
        confirmVariant="default"
        minLength={0}
        reasonLabel="Note"
        onConfirm={(reason) => promote.mutateAsync({ id: release.id, reason: reason || undefined })}
      />
    </>
  );
}
